<template>
    <div id="precall-panel">
        <p v-if="greetingHtml" class="pc-greeting" v-html="greetingHtml"></p>
        <p v-else-if="greeting" class="pc-greeting">{{ greeting }}</p>

        <!-- Tamponlanan mesajlar: henuz sunucuda degil, bellekte duruyor.
             Konusma form gonderilince aciliyor ve bunlar gercek mesaja donuyor. -->
        <p v-for="(text, i) in bufferedMessages" :key="'buf-' + i" class="pc-sent">{{ text }}</p>

        <div v-if="showCTA" class="pc-ctas">
            <button
                v-for="(text, i) in ctaButtons"
                :key="i"
                class="pc-cta"
                :style="{ animationDelay: 90 * i + 'ms' }"
                @mouseenter="onHover"
                @click="start(text)"
            >
                {{ text }}
            </button>
        </div>

        <WelcomeCard
            v-if="showForm"
            ref="card"
            @submit="onFormSubmit"
            @grow="$emit('grow', $event)"
            @touched="cancelFormTimeout"
        />
    </div>
</template>

<script>
import { mapState } from "vuex";
import WelcomeCard from "./WelcomeCard.vue";
import { resolveCTAButtons } from "../../utils/welcomeCTA";
import { resolveWelcomeGreeting } from "../../utils/welcomeGreeting";
import { resolveOpeningMessage } from "../../utils/openingMessage";
import { playTap, playOutgoing, playHover } from "../../utils/sounds";
import { log2 } from "../../utils/log2";

const log = log2("PrecallPanel");

export default {
    name: "PrecallPanel",
    components: { WelcomeCard },
    props: {
        // Form zorunlu mu? Zorunluysa sohbet form gonderilene kadar ACILMIYOR:
        // atama algoritmasi ve chatbot attribute'lari konusma olusurken bu
        // veriye bakiyor, sonradan yazilirsa o mantik bozuluyor.
        formRequired: { type: Boolean, default: false },
        // Tetikleyici karsilamasi: doluysa varsayilan selamlamanin yerine gecer.
        greetingHtml: { type: String, default: "" },
        // panel onizlemesi: form zorunluysa ilk mesaj beklenmeden acik gosterilir, hicbir sey gonderilmez
        preview: { type: Boolean, default: false },
    },
    data() {
        return { bufferedMessages: [], formOpen: this.preview, formTimer: null, opened: false };
    },
    computed: {
        // Ilk mesaj ozel metinse ziyaretci yazmadan once de o gorunur; sohbet acilinca ayni metin ilk mesaj olarak kayitli gelir.
        greeting() {
            return (
                resolveOpeningMessage(this.settings, this.$i18n.locale) ||
                resolveWelcomeGreeting(this.settings, this.$i18n.locale, this.$t("message.welcomeGreeting"))
            );
        },
        // Ayar aciksa form ilk mesaji beklemeden gorunur.
        formOnOpen() {
            const cfg = (this.settings.webchat || {}).formOnOpen;
            return this.formRequired && !!(cfg === true || (cfg && cfg.enabled));
        },
        // Form hemen gorunuyorsa CTA ziyaretciyi ilk yazan yapardi; gizlenir.
        showCTA() {
            return !this.formOnOpen && !this.bufferedMessages.length && !!this.ctaButtons.length;
        },
        showForm() {
            return this.formRequired && (this.formOpen || this.formOnOpen);
        },
        // Yalnizca panelde acilip kaydedilen butonlar gosterilir; hazir oneriler panelde uretiliyor.
        ctaButtons() {
            return resolveCTAButtons(
                (this.settings.webchat || {}).welcomeCTA,
                this.$i18n.locale,
                this.settings.language && this.settings.language.customerClientDefault
            );
        },
        ...mapState({
            settings: (state) => state.setting.settings,
            volume: (state) => state.app.volume,
            soundEnabled: (state) => {
                const n = (state.setting.settings.webchat || {}).notifications;
                return !n || n.sound !== false;
            },
        }),
    },
    watch: {
        // Form gorunur oldugunda cubuk kilitlenmeli; kapaninca serbest kalmali.
        showForm: {
            handler(acik) {
                this.$emit("form-state", !!acik);
                // Form acilista geliyorsa ziyaretci yazamaz; form suresi aciksa sayac form gorundugu an baslar.
                if (acik && this.formOnOpen && !this.formOpen && !this.preview) this.startFormTimeout();
            },
            immediate: true,
        },
    },
    beforeDestroy() {
        this.cancelFormTimeout();
    },
    methods: {
        // Kilitli cubuga dokunuldugunda ChatWindow buraya geliyor.
        shakeForm() {
            if (this.$refs.card) this.$refs.card.shake();
        },
        onHover() {
            playHover({ volume: this.volume, enabled: this.soundEnabled });
        },
        // CTA ya da elle yazilan ilk mesaj. Form varsa sunucuya GITMIYOR:
        // bellekte tutulup form gonderildiginde ilk mesaj olarak yollaniyor.
        start(message) {
            const text = String(message || "").trim();
            if (!text) return;
            playOutgoing({ volume: this.volume, enabled: this.soundEnabled });
            this.bufferedMessages = [...this.bufferedMessages, text];
            this.$emit("grow");
            if (this.formRequired) {
                this.formOpen = true;
                this.startFormTimeout();
                return;
            }
            // Form yoksa sohbet dogrudan aciliyor (anonim bilgilerle).
            this.emitOpen({ loginData: {}, values: null });
        },
        // Ayarda acikken: sure dolarsa sohbet anonim olarak aciliyor.
        // Ziyaretci forma dokunur dokunmaz sayac iptal ediliyor, yoksa
        // yazarken altindan sohbet acilir ve girdigi veri cope giderdi.
        startFormTimeout() {
            const cfg = (this.settings.webchat || {}).welcomeFormTimeout || {};
            if (!cfg.enabled) return;
            const ms = Math.max(10, Number(cfg.seconds) || 60) * 1000;
            this.cancelFormTimeout();
            this.formTimer = setTimeout(() => {
                this.formTimer = null;
                if (this.opened) return;
                log("form timeout, opening anonymously");
                this.emitOpen({ loginData: {}, values: null });
            }, ms);
        },
        cancelFormTimeout() {
            if (this.formTimer) {
                clearTimeout(this.formTimer);
                this.formTimer = null;
            }
        },
        emitOpen({ loginData, values }) {
            if (this.opened) return;
            this.opened = true;
            this.cancelFormTimeout();
            this.$emit("open", {
                initialMessage: this.bufferedMessages[0] || "",
                extraMessages: this.bufferedMessages.slice(1),
                loginData,
                values,
            });
        },
        onFormSubmit({ loginData, values }) {
            log("form submitted, opening chat", loginData);
            playTap({ volume: this.volume, enabled: this.soundEnabled });
            this.emitOpen({ loginData, values });
        },
    },
};
</script>

<style lang="scss" scoped>
// Jivo olcumu: balon saf opaklikla ~250ms beliriyor, CTA'lar kademeli giriyor.
$ease: cubic-bezier(0.22, 0.61, 0.36, 1);

#precall-panel {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: var(--sps-gutter, 12px);

    // #messageContent satir ici "color: white" dayatiyor; miras alan her sey
    // beyaz kaliyor. Renkler burada acikca veriliyor.
    .pc-greeting {
        margin: 0;
        align-self: flex-start;
        max-width: 85%;
        padding: 10px 14px;
        border-radius: var(--sps-radius-lg, 12px);
        background: var(--agent-bubble-color, #f1ecff);
        color: var(--agent-bubble-text-color, #1f1f1f);
        font-size: var(--sps-font-size, 14px);
        animation: sps-fade-up 0.28s $ease backwards;
    }

    // Tamponlanan ziyaretci mesaji; gercek balonla ayni gorunuyor.
    .pc-sent {
        margin: 0;
        align-self: flex-end;
        max-width: 85%;
        padding: 10px 14px;
        border-radius: var(--sps-radius-lg, 12px);
        background: var(--customer-bubble-color, var(--button-color, #7b2ff7));
        color: var(--customer-text-color, #fff);
        font-size: var(--sps-font-size, 14px);
        animation: sps-fade-up 0.28s $ease backwards;
    }

    // Yan yana dizilip sigmayinca alt satira sariyorlar; uzun metinli
    // butonlar zaten kendi satirini kapliyor.
    .pc-ctas {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: flex-end;
    }

    .pc-cta {
        max-width: 100%;
        border: 1px solid var(--button-color, #7b2ff7);
        border-radius: var(--sps-radius-pill, 999px);
        background: var(--background-color-first, #fff);
        color: var(--button-color, #7b2ff7);
        font-size: calc(var(--sps-font-size, 14px) - 1px);
        padding: 8px 14px;
        cursor: pointer;
        text-align: right;
        animation: sps-fade-up 0.3s $ease backwards;
        transition: background-color 0.16s $ease, color 0.16s $ease, box-shadow 0.16s $ease;

        // Hover'da konum degistirmiyor: giris animasyonu fill-mode ile
        // transform'u tutuyordu ve ikisi cakisip butonu ziplatiyordu.
        &:hover {
            background: var(--button-color, #7b2ff7);
            color: var(--button-text-color, #fff);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        }

        &:active {
            box-shadow: none;
        }
    }
}

@keyframes sps-fade-up {
    from {
        opacity: 0;
        transform: translateY(6px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    #precall-panel .pc-greeting,
    #precall-panel .pc-sent,
    #precall-panel .pc-cta {
        animation: none;
        transition: none;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/V2/PrecallPanel.vue